import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Mail, Lock, Eye, Clock, FileText } from 'lucide-react';

const PrivacyPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-[#060606] pt-48 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#C9A84C]/20 mb-6">
            <Shield className="w-8 h-8 text-[#C9A84C]" />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-[#F5F0E8] mb-4">
            Politique de Confidentialité
          </h1>
          <p className="text-[#B9B2A8]">
            Dernière mise à jour : 10 février 2025
          </p>
        </div>

        {/* Introduction */}
        <div className="prose prose-invert max-w-none mb-12">
          <p className="text-[#B9B2A8] leading-relaxed">
            Bienvenue sur l'application Invasion Latina. Nous nous engageons à protéger votre vie privée et vos données personnelles conformément au Règlement Général sur la Protection des Données (RGPD). Cette politique de confidentialité explique comment nous collectons, utilisons, partageons et protégeons vos informations lorsque vous utilisez notre application mobile.
          </p>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          {/* Section 1 */}
          <section className="bg-[#0B0B0C] border border-white/10 rounded-xl p-6 sm:p-8">
            <h2 className="font-display text-2xl text-[#C9A84C] mb-4">1. Responsable du Traitement</h2>
            <div className="text-[#B9B2A8] space-y-2">
              <p><strong className="text-[#F5F0E8]">Nom de l'entreprise :</strong> Invasion Latina</p>
              <p><strong className="text-[#F5F0E8]">Adresse :</strong> Bruxelles, Belgique</p>
              <p><strong className="text-[#F5F0E8]">Email de contact :</strong> <a href="mailto:info@invasionlatina.be" className="text-[#C9A84C] hover:underline">info@invasionlatina.be</a></p>
              <p><strong className="text-[#F5F0E8]">Site web :</strong> <a href="https://www.invasionlatina.be" className="text-[#C9A84C] hover:underline">www.invasionlatina.be</a></p>
            </div>
          </section>

          {/* Section 2 */}
          <section className="bg-[#0B0B0C] border border-white/10 rounded-xl p-6 sm:p-8">
            <h2 className="font-display text-2xl text-[#C9A84C] mb-4">2. Données Personnelles Collectées</h2>
            
            <h3 className="font-display text-xl text-[#F5F0E8] mb-3 mt-6">2.1 Données que vous nous fournissez directement</h3>
            <ul className="list-disc list-inside text-[#B9B2A8] space-y-2">
              <li><strong className="text-[#F5F0E8]">Informations d'inscription :</strong> nom, prénom, adresse email, mot de passe (stocké de manière chiffrée)</li>
              <li><strong className="text-[#F5F0E8]">Photo de profil :</strong> si vous choisissez d'en télécharger une</li>
              <li><strong className="text-[#F5F0E8]">Préférences de communication :</strong> vos choix concernant les notifications et communications</li>
              <li><strong className="text-[#F5F0E8]">Demandes de chansons :</strong> titres de chansons et noms d'artistes demandés</li>
              <li><strong className="text-[#F5F0E8]">Réservations :</strong> détails de vos réservations de tables (date, type, nombre de personnes)</li>
            </ul>

            <h3 className="font-display text-xl text-[#F5F0E8] mb-3 mt-6">2.2 Données collectées automatiquement</h3>
            <ul className="list-disc list-inside text-[#B9B2A8] space-y-2">
              <li><strong className="text-[#F5F0E8]">Données de localisation :</strong> position GPS lors des événements (uniquement avec votre consentement explicite)</li>
              <li><strong className="text-[#F5F0E8]">Identifiants de l'appareil :</strong> modèle, système d'exploitation, identifiants uniques pour les notifications push</li>
              <li><strong className="text-[#F5F0E8]">Token de notification push :</strong> identifiant technique permettant l'envoi de notifications sur votre appareil</li>
              <li><strong className="text-[#F5F0E8]">Données d'utilisation :</strong> pages visitées, fonctionnalités utilisées, dates et heures de connexion</li>
            </ul>

            <h3 className="font-display text-xl text-[#F5F0E8] mb-3 mt-6">2.3 Données du programme de fidélité</h3>
            <ul className="list-disc list-inside text-[#B9B2A8] space-y-2">
              <li>Nombre d'Invasion Coins accumulés</li>
              <li>Historique de participation aux événements</li>
              <li>Récompenses utilisées et dates d'utilisation</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="bg-[#0B0B0C] border border-white/10 rounded-xl p-6 sm:p-8">
            <h2 className="font-display text-2xl text-[#C9A84C] mb-4">3. Utilisation de vos Données</h2>
            <p className="text-[#B9B2A8] mb-4">Nous utilisons vos données personnelles pour les finalités suivantes :</p>
            <div className="overflow-x-auto">
              <table className="w-full border border-white/10">
                <thead>
                  <tr className="bg-white/5">
                    <th className="border border-white/10 px-4 py-3 text-left text-[#F5F0E8]">Finalité</th>
                    <th className="border border-white/10 px-4 py-3 text-left text-[#F5F0E8]">Base légale</th>
                  </tr>
                </thead>
                <tbody className="text-[#B9B2A8]">
                  <tr>
                    <td className="border border-white/10 px-4 py-3">Créer et gérer votre compte utilisateur</td>
                    <td className="border border-white/10 px-4 py-3">Exécution du contrat</td>
                  </tr>
                  <tr>
                    <td className="border border-white/10 px-4 py-3">Gérer le programme de fidélité Invasion Coins</td>
                    <td className="border border-white/10 px-4 py-3">Exécution du contrat</td>
                  </tr>
                  <tr>
                    <td className="border border-white/10 px-4 py-3">Traiter vos demandes de chansons et réservations</td>
                    <td className="border border-white/10 px-4 py-3">Exécution du contrat</td>
                  </tr>
                  <tr>
                    <td className="border border-white/10 px-4 py-3">Envoyer des notifications push (événements, promotions, rappels)</td>
                    <td className="border border-white/10 px-4 py-3">Consentement</td>
                  </tr>
                  <tr>
                    <td className="border border-white/10 px-4 py-3">Communications marketing et promotionnelles</td>
                    <td className="border border-white/10 px-4 py-3">Consentement</td>
                  </tr>
                  <tr>
                    <td className="border border-white/10 px-4 py-3">Personnaliser votre expérience dans l'application</td>
                    <td className="border border-white/10 px-4 py-3">Intérêt légitime</td>
                  </tr>
                  <tr>
                    <td className="border border-white/10 px-4 py-3">Améliorer nos services et développer de nouvelles fonctionnalités</td>
                    <td className="border border-white/10 px-4 py-3">Intérêt légitime</td>
                  </tr>
                  <tr>
                    <td className="border border-white/10 px-4 py-3">Assurer la sécurité de l'application et prévenir la fraude</td>
                    <td className="border border-white/10 px-4 py-3">Intérêt légitime</td>
                  </tr>
                  <tr>
                    <td className="border border-white/10 px-4 py-3">Respecter nos obligations légales</td>
                    <td className="border border-white/10 px-4 py-3">Obligation légale</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 4 */}
          <section className="bg-[#0B0B0C] border border-white/10 rounded-xl p-6 sm:p-8">
            <h2 className="font-display text-2xl text-[#C9A84C] mb-4">4. Notifications Push et Communications</h2>
            <div className="bg-[#C9A84C]/10 border border-[#C9A84C]/30 rounded-lg p-4 mb-4">
              <p className="text-[#F5F0E8] font-medium">✅ Votre consentement est requis</p>
              <p className="text-[#B9B2A8] text-sm mt-2">Lors de l'installation de l'application ou lors de votre première connexion, nous vous demanderons votre autorisation pour vous envoyer des notifications push. Vous pouvez modifier ce choix à tout moment.</p>
            </div>

            <h3 className="font-display text-xl text-[#F5F0E8] mb-3 mt-6">4.1 Types de notifications</h3>
            <ul className="list-disc list-inside text-[#B9B2A8] space-y-2">
              <li><strong className="text-[#F5F0E8]">Notifications transactionnelles :</strong> confirmations de réservation, rappels d'événements</li>
              <li><strong className="text-[#F5F0E8]">Notifications promotionnelles :</strong> annonces de nouveaux événements, offres spéciales</li>
              <li><strong className="text-[#F5F0E8]">Notifications de service :</strong> mises à jour importantes de l'application</li>
            </ul>

            <h3 className="font-display text-xl text-[#F5F0E8] mb-3 mt-6">4.2 Comment gérer vos préférences</h3>
            <ul className="list-disc list-inside text-[#B9B2A8] space-y-2">
              <li>Désactiver toutes les notifications : dans les paramètres de votre téléphone</li>
              <li>Gérer vos préférences : dans la section "Paramètres" de l'application</li>
              <li>Vous désabonner des emails : via le lien dans chaque email</li>
            </ul>
          </section>

          {/* Section 10 */}
          <section className="bg-[#0B0B0C] border border-white/10 rounded-xl p-6 sm:p-8">
            <h2 className="font-display text-2xl text-[#C9A84C] mb-4">10. Vos Droits (RGPD)</h2>
            <p className="text-[#B9B2A8] mb-4">Conformément au Règlement Général sur la Protection des Données, vous disposez des droits suivants :</p>
            <ul className="list-disc list-inside text-[#B9B2A8] space-y-2">
              <li><strong className="text-[#F5F0E8]">Droit d'accès :</strong> obtenir une copie de toutes vos données personnelles</li>
              <li><strong className="text-[#F5F0E8]">Droit de rectification :</strong> corriger les données inexactes ou incomplètes</li>
              <li><strong className="text-[#F5F0E8]">Droit à l'effacement :</strong> demander la suppression de vos données ("droit à l'oubli")</li>
              <li><strong className="text-[#F5F0E8]">Droit à la limitation :</strong> restreindre le traitement de vos données</li>
              <li><strong className="text-[#F5F0E8]">Droit à la portabilité :</strong> recevoir vos données dans un format structuré</li>
              <li><strong className="text-[#F5F0E8]">Droit d'opposition :</strong> vous opposer au traitement pour le marketing direct</li>
              <li><strong className="text-[#F5F0E8]">Droit de retirer votre consentement :</strong> à tout moment</li>
            </ul>
            <div className="mt-6 p-4 bg-white/5 rounded-lg">
              <p className="text-[#F5F0E8] font-medium mb-2">Pour exercer vos droits :</p>
              <p className="text-[#B9B2A8] text-sm">Envoyez un email à <a href="mailto:info@invasionlatina.be" className="text-[#C9A84C] hover:underline">info@invasionlatina.be</a> avec une copie de votre pièce d'identité. Nous répondrons dans un délai de 30 jours.</p>
            </div>
          </section>

          {/* Section 13 */}
          <section className="bg-[#0B0B0C] border border-white/10 rounded-xl p-6 sm:p-8">
            <h2 className="font-display text-2xl text-[#C9A84C] mb-4">13. Mineurs</h2>
            <p className="text-[#B9B2A8]">
              L'application Invasion Latina est destinée aux personnes âgées de <strong className="text-[#F5F0E8]">18 ans et plus</strong>, conformément à la nature de nos événements (vie nocturne, consommation d'alcool possible). Nous ne collectons pas sciemment de données concernant des mineurs. Si nous découvrons qu'un mineur nous a fourni des données personnelles, nous les supprimerons immédiatement.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-gradient-to-br from-[#C9A84C]/20 to-transparent border border-[#C9A84C]/30 rounded-xl p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <Mail className="w-6 h-6 text-[#C9A84C] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-display text-xl text-[#F5F0E8] mb-2">Nous Contacter</h3>
                <p className="text-[#B9B2A8] mb-4">
                  Pour toute question concernant cette politique de confidentialité, vos données personnelles, ou pour exercer vos droits :
                </p>
                <ul className="space-y-2 text-[#B9B2A8]">
                  <li><strong className="text-[#F5F0E8]">Email :</strong> <a href="mailto:info@invasionlatina.be" className="text-[#C9A84C] hover:underline">info@invasionlatina.be</a></li>
                  <li><strong className="text-[#F5F0E8]">Site web :</strong> <a href="https://www.invasionlatina.be" className="text-[#C9A84C] hover:underline">www.invasionlatina.be</a></li>
                  <li><strong className="text-[#F5F0E8]">Instagram :</strong> <a href="https://instagram.com/invasionlatina.be" className="text-[#C9A84C] hover:underline">@invasionlatina.be</a></li>
                </ul>
              </div>
            </div>
          </section>

          {/* APD */}
          <section className="bg-[#0B0B0C] border border-white/10 rounded-xl p-6 sm:p-8">
            <h3 className="font-display text-xl text-[#F5F0E8] mb-4">🏛️ Autorité de Contrôle</h3>
            <p className="text-[#B9B2A8] mb-4">
              Si vous estimez que le traitement de vos données personnelles constitue une violation du RGPD, vous avez le droit d'introduire une réclamation auprès de :
            </p>
            <div className="text-[#B9B2A8] space-y-1">
              <p><strong className="text-[#F5F0E8]">Autorité de Protection des Données (APD)</strong></p>
              <p>Rue de la Presse 35, 1000 Bruxelles</p>
              <p>Site : <a href="https://www.autoriteprotectiondonnees.be" className="text-[#C9A84C] hover:underline">www.autoriteprotectiondonnees.be</a></p>
              <p>Email : contact@apd-gba.be</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;